import { AccessComparisonExportButton } from './AccessComparisonExportButton';
import { AccessExplainerObjectQueryParams } from './AccessExplainerFields';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { getDataAccessRoute } from '@lib/routes';
import { isEqual } from 'lodash';
import { SonarAlert } from '@ui/SonarAlert';

import {
	AccessComparisonObjectQueryParams,
	useAction,
	useAddComparison,
	useFilteredAccessComparisonData,
	usePeriscopeActions,
	useQueryParams,
	useSonarSelector,
	useTableFiltering
} from '@hooks';
import { AccessExplainerField, AccessType, coreActions } from '@sonar-software/sonar-core';
import { arrayify, parseQueryString } from '@sonar-software/sonar-ts-lib';
import { convertAccessFieldToDql, FieldTable } from './fields/FieldTable';
import { DynamicFilter, SonarObjectType } from '@sonar-software/sonar-ts-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

const accessComparisonFieldDataSelectors =
	createAsyncSelectors((state) => state.core.dataSecurity.dataAccess.accessComparison.object.get);

const convertToDql = (params: AccessComparisonObjectQueryParams) => {
	const { accessId, accessType, objectName, sortBy, sortDirection, ...rest } = params;

	return convertAccessFieldToDql(sortBy, sortDirection, rest as DynamicFilter);
};

export const AccessComparisonFields = () => {
	const {
		createForceRefreshHandler,
		createRemoveColumnHandler,
		handleAdditionalColumnsChange,
		hasNewColumns,
		isRemovingColumn,
		isTriggeredByRefresh,
		prepareApiParams,
		resetColumnRefs,
		setHasNewColumns
	} = useAddComparison();

	const { objectId } = useParams<{ objectId: string }>();
	const lastUsedParams = useRef<AccessComparisonObjectQueryParams>();

	const navigate = useNavigate();
	const location = useLocation();
	const { openPeriscope } = usePeriscopeActions();

	const getComparisonObject = useAction(coreActions.requestGetCoreDataSecurityAccessComparisonByObjectId);
	const data = useSonarSelector(accessComparisonFieldDataSelectors.data);
	const isRequested = useSonarSelector(accessComparisonFieldDataSelectors.isRequested);

	useEffect(() => {
		if (isTriggeredByRefresh.current && !isRequested) {
			isTriggeredByRefresh.current = false;
		}
	}, [ isTriggeredByRefresh, isRequested ]);


	const parsedQuery = useMemo(
		() => parseQueryString(location.search) as AccessComparisonObjectQueryParams,
		[ location.search ]
	);

	const initialParams = useMemo(
		() => ({
			displays: '',
			filters: '',
			ordering: '',
			...parsedQuery
		}),
		[ parsedQuery ]
	);

	const updateUrl = useCallback(
		(queryObject: AccessComparisonObjectQueryParams) => {
			navigate(
				getDataAccessRoute({
					objectName: objectId,
					...queryObject
				}),
				{ replace: true }
			);
		}, [ navigate, objectId ]
	);

	const { queryParams, updateParams: originalUpdateParams } = useQueryParams<AccessComparisonObjectQueryParams>({
		defaultParams: initialParams,
		onUpdate: (_, __, combined) => {
			const urlParams: AccessComparisonObjectQueryParams = {
				accessId: combined.accessId ?? [],
				accessType: combined.accessType ?? [],
				displays: combined.displays ?? '',
				filters: combined.filters ?? '',
				objectName: combined.objectName ?? '',
				ordering: combined.ordering ?? ''
			};

			updateUrl(urlParams);
		}
	});

	const { isFiltering, updateParams } = useTableFiltering(originalUpdateParams, isRequested);

	const handleRemoveColumn = useMemo(() =>
		createRemoveColumnHandler(queryParams, updateUrl, updateParams),
	[ createRemoveColumnHandler, queryParams, updateUrl, updateParams ]);

	const callApi = useCallback((params?: AccessComparisonObjectQueryParams) => {
		const { accessId, accessType, displays, objectName, ordering, ...rest } = params ?? {};

		if (!objectId || !accessId?.length || !accessType?.length) {
			return;
		}

		const baseIds = arrayify(accessId);
		const baseTypes = arrayify(accessType);
		const { accessIdList, accessTypeList, extraColumns } = prepareApiParams(baseIds, baseTypes);

		const combinedParams: AccessComparisonObjectQueryParams = {
			accessId: accessIdList,
			accessType: accessTypeList,
			filters: params?.filters ?? '',
			objectName: params?.objectName ?? '',
			ordering: params?.ordering ?? ''
		};

		lastUsedParams.current = combinedParams;

		if (extraColumns.length) {
			updateUrl(combinedParams);
		}

		getComparisonObject({
			accessId: accessIdList,
			accessType: accessTypeList,
			objectId,
			...rest
		});

		if (extraColumns.length) {
			resetColumnRefs();
		}
	}, [ objectId,
		getComparisonObject,
		updateUrl,
		prepareApiParams,
		resetColumnRefs ]);

	useEffect(() => {
		// Skip parameter sync while removing a column to prevent race conditions
		if (isRemovingColumn.current) {
			return;
		}

		if (!isEqual(queryParams.accessId, parsedQuery.accessId) ||
            !isEqual(queryParams.accessType, parsedQuery.accessType) ||
            parsedQuery.objectName !== queryParams.objectName
		) {
			updateParams({
				accessId: parsedQuery.accessId,
				accessType: parsedQuery.accessType,
				objectName: parsedQuery.objectName
			});
		}
	}, [ parsedQuery, queryParams, updateParams, isRemovingColumn ]);

	useEffect(() => {
		if (!queryParams.accessType || queryParams.accessType.length === 0 ||
            !queryParams.accessId || queryParams.accessId.length === 0) {
			navigate(
				getDataAccessRoute(),
				{ replace: true }
			);
		}

		if (isRemovingColumn.current) {
			isRemovingColumn.current = false;

			return;
		}

		const lastUsed = lastUsedParams.current;

		if (lastUsed &&
		    queryParams.filters === lastUsed.filters &&
            isEqual(arrayify(queryParams.accessId), lastUsed.accessId) &&
            isEqual(arrayify(queryParams.accessType), lastUsed.accessType) &&
            queryParams.objectName === lastUsed.objectName) {
			return;
		}

		callApi(queryParams);
	}, [ callApi, navigate, objectId, queryParams, isRemovingColumn ]);

	const handleAccessChange = useCallback(
		(name: string, type: AccessType, value: string) => {
			if (type === AccessType.Object) {
				const queryObject: AccessComparisonObjectQueryParams = {
					accessId: queryParams.accessId,
					accessType: queryParams.accessType,
					objectName: name
				};

				navigate(
					getDataAccessRoute({
						objectName: value,
						...queryObject
					}),
					{ replace: true }
				);

				return;
			}

			const queryObject: AccessExplainerObjectQueryParams = {
				accessName: name,
				objectName: queryParams.objectName,
				type
			};

			navigate(
				getDataAccessRoute({
					id: value,
					objectName: objectId,
					...queryObject
				}),
				{ replace: true }
			);

		}, [ navigate, objectId, queryParams ]
	);

	const comparisonDataFilteredByParams = useFilteredAccessComparisonData(data, queryParams, 'fields');

	const handleRowConfig = useCallback(
		(record: AccessExplainerField) => ({
			onClick: () => {
				openPeriscope({
					id: record.sonarObjectId,
					name: record.fieldName,
					type: SonarObjectType.SalesforceField
				});
			}
		}),
		[ openPeriscope ]
	);

	return (
		<FieldTable
			convertToDql={
				(params: AccessComparisonObjectQueryParams) => convertToDql(params)
			}
			data={ comparisonDataFilteredByParams }
			defaultAccessSearchName={ queryParams.objectName }
			displayNameString={ queryParams.displays }
			exportButton={
				<AccessComparisonExportButton
					queryParams={ {
						...queryParams,
						objectId
					} } />
			}
			filterString={ queryParams.filters }
			highlightDifferences
			isFiltering={ isFiltering.current }
			isLoading={ isRequested }
			isRefreshing={ isTriggeredByRefresh.current }
			notificationBar={
				<SonarAlert
					active={ hasNewColumns }
					afterClose={ () => setHasNewColumns(false) }
					closable
					message={ COPY_SPECIFIC.DATA_OUT_OF_DATE }
					showIcon
					style={ { marginBottom: 16 } }
					type='warning' />
			}
			onAccessChange={ handleAccessChange }
			onAdditionalColumnsChange={ handleAdditionalColumnsChange }
			onForceRefresh={ createForceRefreshHandler(callApi, queryParams) }
			onRemoveColumn={ handleRemoveColumn }
			onRowConfig={ handleRowConfig }
			orderString={ queryParams.ordering }
			showAddPermissionButton
			showRemoveColumn
			title={ COPY_SPECIFIC.ACCESS_COMPARISON }
			updateParams={ updateParams } />
	);
};
